import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Menu, X, Phone, Lock, User, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

// Try to import logo from icons folder, but provide fallback if it doesn't exist
let logo;
try {
  logo = require('../../assets/icons/logo.png');
} catch (error) {
  logo = null;
}

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #7F1B1C;
  z-index: 1000;
  padding: 0;
  border-top: 2px solid #640003;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 10px;
  height: 80px;

  @media (max-width: 768px) {
    padding: 0 10px;
    height: 70px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  justify-content: stretch;
  margin-left: 5px;

  @media (max-width: 768px) {
    margin-left: 5px;
  }
`;

const LogoImage = styled.img`
  height: 100%;
  width: auto;
  object-fit: contain;
  margin-left: 5px;

  @media (max-width: 768px) {
    margin-left: 5px;
  }
`;

const FallbackLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  justify-content: stretch;
`;

const FallbackText = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  font-family: 'Arial', sans-serif;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FallbackSubtext = styled.div`
  font-size: 0.6rem;
  color: #ccc;
  font-weight: 300;
  margin-top: -0.1rem;

  @media (max-width: 768px) {
    font-size: 0.5rem;
  }
`;

const CenterMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 60px;

  @media (max-width: 768px) {
    margin-left: 0;
    flex: 1;
    justify-content: flex-end;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopNavLink = styled(Link)`
  color: #ccc;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 0.5rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;

  &:hover {
    color: white;
  }

  &.active {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const NavDropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 11rem;
  background: #640003;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 6px 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-6px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
  pointer-events: none;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
  z-index: 1100;
`;

const NavDropdownRoot = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;

  &:hover ${NavDropdownPanel},
  &:focus-within ${NavDropdownPanel} {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const PayParkingTrigger = styled(DesktopNavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
`;

const NavDropdownItem = styled(Link)`
  display: block;
  padding: 0.55rem 1rem;
  color: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MenuButton = styled.button`
  width: 40px;
  height: 40px;
  background: #2F4F4F;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #1a2f2f;
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const LoginButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s ease;
  padding: 0.5rem;

  &:hover {
    color: black;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.25rem;
    gap: 0.25rem;
  }
`;

const LoginIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #C31321;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
  }
`;

const PhoneIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #8B0000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const PhoneNumber = styled.div`
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(139, 0, 0, 0.98);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 80px;
  gap: 1.5rem;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
`;

const MobileMenuHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: rgba(139, 0, 0, 0.95);
`;

const MobileMenuTitle = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
`;

const MobileMenuLink = styled(Link)`
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  width: 80%;
  text-align: center;

  &:hover {
    color: #FFD700;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MobileMenuSubLink = styled(Link)`
  color: rgba(255, 255, 255, 0.92);
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease, background 0.3s ease;
  padding: 0.55rem 2rem 0.55rem 3rem;
  border-radius: 8px;
  width: 80%;
  text-align: left;

  &:hover {
    color: #ffd700;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MobilePhoneNumber = styled.div`
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  width: 80%;
  background: rgba(255, 255, 255, 0.1);
  margin-top: 0.5rem;
`;

const MobilePhoneIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #8B0000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
`;

const CloseMenuButton = styled.button`
  width: 40px;
  height: 40px;
  background: #2F4F4F;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #1a2f2f;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;

  @media (max-width: 480px) {
    width: 95%;
    max-width: 350px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 0;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 480px) {
    padding: 1rem 1rem 0;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  color: ${props => props.active ? '#1e293b' : '#9ca3af'};
  font-weight: ${props => props.active ? '600' : '400'};

  &:hover {
    color: #1e293b;
  }

  ${props => props.active && `
    border-bottom-color: #C31321;
  `}

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }
`;

const TabIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.active ? '#1e293b' : '#9ca3af'};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: #f3f4f6;
    color: #1e293b;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Required = styled.span`
  color: #ef4444;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #C31321;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ForgotPassword = styled.a`
  color: #C31321;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const RememberMe = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #C31321;
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ModalLoginButton = styled.button`
  width: 100%;
  background: #C31321;
  color: white;
  border: none;
  padding: 0.875rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: #a3121a;
  }
`;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Prevent body scroll when mobile menu is open
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    if (confirmPassword && value !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    if (password && value !== password) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  const payParkingActive =
    location.pathname === '/smart-parking' || location.pathname === '/smart-parking/open-tickets';

  // Clean up body overflow when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <HeaderContainer>
      <Nav>
        <LogoContainer>
          {logo ? (
            <LogoImage src={logo} alt="Keyplot Investments Logo" />
          ) : (
            <FallbackLogo>
              <FallbackText>KPI</FallbackText>
              <FallbackSubtext>Keyplot Investments Logo</FallbackSubtext>
            </FallbackLogo>
          )}
        </LogoContainer>
        
        <CenterMenu>
          <DesktopNav>
            <DesktopNavLink to="/" className={isActive('/') ? 'active' : ''}>Home</DesktopNavLink>
            <DesktopNavLink to="/about" className={isActive('/about') ? 'active' : ''}>About Us</DesktopNavLink>
            <DesktopNavLink to="/parking-options" className={isActive('/parking-options') ? 'active' : ''}>Parking Rates</DesktopNavLink>
            <DesktopNavLink to="/kp-card" className={isActive('/kp-card') ? 'active' : ''}>KP Card</DesktopNavLink>
            <NavDropdownRoot>
              <PayParkingTrigger to="/smart-parking" className={payParkingActive ? 'active' : ''}>
                Pay Parking
                <ChevronDown size={14} aria-hidden />
              </PayParkingTrigger>
              <NavDropdownPanel>
                <NavDropdownItem to="/smart-parking/open-tickets">Open Tickets</NavDropdownItem>
              </NavDropdownPanel>
            </NavDropdownRoot>
            <DesktopNavLink to="/careers" className={isActive('/careers') ? 'active' : ''}>Careers</DesktopNavLink>
            <DesktopNavLink to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact Us</DesktopNavLink>
          </DesktopNav>
          <MenuButton onClick={toggleMobileMenu}>
            <Menu size={20} />
          </MenuButton>
        </CenterMenu>

        <ContactInfo>
          <LoginButton onClick={() => setIsLoginModalOpen(true)}>
            <LoginIcon>
              <Lock size={12} />
            </LoginIcon>
            Login
          </LoginButton>
          <PhoneIcon>
            <Phone size={12} />
          </PhoneIcon>
          <PhoneNumber>+264 857 208 426 / +264 61 277 824</PhoneNumber>
        </ContactInfo>
      </Nav>

              <MobileMenu isOpen={isMobileMenuOpen}>
          <MobileMenuHeader>
            <MobileMenuTitle>Menu</MobileMenuTitle>
            <CloseMenuButton onClick={closeMobileMenu}>
              <X size={20} />
            </CloseMenuButton>
          </MobileMenuHeader>
          <MobileMenuLink to="/" onClick={closeMobileMenu}>Home</MobileMenuLink>
          <MobileMenuLink to="/about" onClick={closeMobileMenu}>About Us</MobileMenuLink>
          <MobileMenuLink to="/parking-options" onClick={closeMobileMenu}>Parking Rates</MobileMenuLink>
          <MobileMenuLink to="/kp-card" onClick={closeMobileMenu}>KP Card</MobileMenuLink>
          <MobileMenuLink to="/smart-parking" onClick={closeMobileMenu}>Pay Parking</MobileMenuLink>
          <MobileMenuSubLink to="/smart-parking/open-tickets" onClick={closeMobileMenu}>
            Open Tickets
          </MobileMenuSubLink>
          <MobileMenuLink to="/careers" onClick={closeMobileMenu}>Careers</MobileMenuLink>
          <MobileMenuLink to="/contact" onClick={closeMobileMenu}>Contact Us</MobileMenuLink>
          <MobilePhoneNumber>
            <MobilePhoneIcon>
              <Phone size={12} />
            </MobilePhoneIcon>
            +264 857 208 426 / +264 61 277 824
          </MobilePhoneNumber>
        </MobileMenu>

      {isLoginModalOpen && (
        <ModalOverlay onClick={() => setIsLoginModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <TabContainer>
                <Tab 
                  active={activeTab === 'login'} 
                  onClick={() => setActiveTab('login')}
                >
                  <TabIcon active={activeTab === 'login'}>
                    <Lock size={16} />
                  </TabIcon>
                  LOGIN
                </Tab>
                <Tab 
                  active={activeTab === 'register'} 
                  onClick={() => setActiveTab('register')}
                >
                  <TabIcon active={activeTab === 'register'}>
                    <User size={16} />
                  </TabIcon>
                  REGISTER
                </Tab>
              </TabContainer>
              <CloseButton onClick={() => setIsLoginModalOpen(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              {activeTab === 'login' ? (
                <>
                  <FormGroup>
                    <Label>
                      Email <Required>*</Required>
                    </Label>
                    <Input type="email" placeholder="Enter Your Email" />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>
                      Password <Required>*</Required>
                    </Label>
                    <Input type="password" placeholder="Enter your password" />
                  </FormGroup>
                  
                  <OptionsContainer>
                    <ForgotPassword href="#">Forgot password?</ForgotPassword>
                    <RememberMe>
                      <Checkbox type="checkbox" id="remember" />
                      <label htmlFor="remember">Remember me</label>
                    </RememberMe>
                  </OptionsContainer>
                  
                  <ModalLoginButton>Login</ModalLoginButton>
                </>
              ) : (
                <>
                  <FormGroup>
                    <Label>
                      Email <Required>*</Required>
                    </Label>
                    <Input type="email" placeholder="Enter Your Email" />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>
                      Create Password <Required>*</Required>
                    </Label>
                    <Input 
                      type="password" 
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>
                      Confirm Your Password <Required>*</Required>
                    </Label>
                    <Input 
                      type="password" 
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    />
                    {passwordError && <ErrorMessage>{passwordError}</ErrorMessage>}
                  </FormGroup>
                  
                  <ModalLoginButton>Register</ModalLoginButton>
                </>
              )}
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </HeaderContainer>
  );
};

export default Header; 
